import './index.css'

import * as Sentry from '@sentry/react'
import { initGtag } from './lib/gtag'

// Polyfill URL.parse for Safari < 18 (used by pdfjs-dist 5.x)
const urlCtor = URL as typeof URL & {
  parse?: (url: string | URL, base?: string | URL) => URL | null
}
if (typeof urlCtor.parse !== 'function') {
  urlCtor.parse = (url, base) => {
    try {
      return new URL(url, base)
    } catch {
      return null
    }
  }
}

const sentryDsn = import.meta.env.VITE_SENTRY_DSN
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.VITE_APP_ENV || 'local',
    integrations: [
      // Masking is stated explicitly rather than relying on the SDK default.
      // Replay covers the public share link, where an anonymous investor types
      // free-text feedback about a named startup and its founders — a replay
      // that captured it could not be un-captured after the fact.
      Sentry.replayIntegration({ maskAllText: true, maskAllInputs: true, blockAllMedia: true }),
    ],
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
  })
}

initGtag()

// Setup error handlers
import './lib/logger'

import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { client } from '@workspace/api-backend/client'
import { createRoot } from 'react-dom/client'
import { queryClient } from './lib/query-client'
import { router } from './router'

client.setConfig({
  baseUrl: import.meta.env.VITE_BACKEND_URL,
  credentials: 'include',
})

const rootElement = document.getElementById('root')

// NOT wrapped in <StrictMode>, deliberately.
//
// StrictMode double-invokes effects in development. `useLocalRuntime`
// (@assistant-ui/core 0.3.17, the newest published) has a cleanup that calls
// `detach()` on the thread runtime and no matching re-attach on the second
// setup — and `detach()` nulls the run queue, so every `append` afterwards
// writes to a dead runtime. The result is an "Ask the profile" pane that mounts
// and then stays permanently empty, with no error and no network request, in
// dev only. Production never double-invokes, so it was always fine there, which
// is what made this so confusing to chase.
//
// Restore this the moment assistant-ui fixes that cleanup — the double-render
// checks are worth having, and nothing else in this app needs them off.
if (rootElement) {
  createRoot(rootElement).render(
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  )
}
